<template>
  <BlockContainer class="p-5">
    <h5>Overtakelsesprotokoller</h5>

    <div v-if="!loaded" class="text-center mt-5 pt-5 mb-5 pb-5">
      <hl-Loader />
    </div>
    <div v-else>
      <div v-if="isLoadingTops" class="text-center mt-5 pt-3 mb-3">
        <hl-Loader />
        <p class="mt-2 text-muted">Laster overtakelsespprotokoller...</p>
      </div>

      <div v-else-if="hasErrors" class="mt-5">
        <hl-Alert variant="danger" layout="vertical">
          <p>{{ topsError || newInspectionReportsError || 'En feil oppstod under lasting av protokoller' }}</p>
          <button class="btn btn-sm btn-outline-danger" @click="fetchAllData">
            Prøv igjen
          </button>
        </hl-Alert>
      </div>

      <div v-else-if="allTops.length" class="table-responsive mt-5">
        <table class="table table-hover">
          <thead>
            <tr>
              <th scope="col">Påbegynt</th>
              <th scope="col">Leietaker</th>
              <th scope="col">Status</th>
              <th scope="col" class="text-center">Vis / endre</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="top in allTops" :key="top.id">
              <td scope="row">
                <hl-Date v-if="top.startDate" :date="top.startDate" />
                <p v-else>Ikke satt</p>
              </td>
              <td>{{ top.tenant }}</td>
              <td>{{ mapStatuses(top.status) }}</td>
              <td class="text-center">
                <a v-if="top.uri" :href="top.uri" class="see-edit" data-cy="top-uri">
                  <i class="budicon-chevron-right-circle"></i>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="mt-5">
        <hl-Alert variant="info" layout="vertical">
          <p>Leietakere på vei inn? Bruk en overtakelsesprotokoll for å sikre en trygg overgang.</p>
          <button class="btn btn-sm btn-light" @click="createNewTopInspectionReport">
            Ny overtakelsesprotokoll
          </button>
        </hl-Alert>
      </div>
    </div>

    <template v-if="loaded">
      <h5 class="mt-5">Utflyttingsprotokoller</h5>
      <div>
        <div v-if="isLoadingMoveouts" class="text-center mt-5 pt-3 mb-3">
          <hl-Loader />
          <p class="mt-2 text-muted">Laster utflyttingsprotokoller...</p>
        </div>
  
        <div v-else-if="newInspectionReportsError" class="mt-5">
          <hl-Alert variant="danger" layout="vertical">
            <p>{{ newInspectionReportsError || 'En feil oppstod under lasting av utflyttingsprotokoller' }}</p>
            <button class="btn btn-sm btn-outline-danger" @click="fetchAllData">
              Prøv igjen
            </button>
          </hl-Alert>
        </div>
  
        <div v-else-if="moveoutProtocols.length" class="table-responsive mt-5">
          <table class="table table-hover">
            <thead>
              <tr>
                <th scope="col">Påbegynt</th>
                <th scope="col">Leietaker</th>
                <th scope="col">Status</th>
                <th scope="col" class="text-center">Vis / endre</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="moveout in moveoutProtocols" :key="moveout.id">
                <td scope="row">
                  <hl-Date v-if="moveout.startDate" :date="moveout.startDate" />
                  <p v-else>Ikke satt</p>
                </td>
                <td>{{ moveout.tenant }}</td>
                <td>{{ mapStatuses(moveout.status) }}</td>
                <td class="text-center">
                  <a v-if="moveout.uri" :href="moveout.uri" class="see-edit" data-cy="moveout-uri">
                    <i class="budicon-chevron-right-circle"></i>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
  
        <div v-else class="mt-5">
            <p>Ingen utflyttingsprotokoller.</p>
            
        </div>
      </div>
    </template>
  </BlockContainer>
</template>

<script>
import { mapGetters } from 'vuex';
import BlockContainer from '@/components/BlockContainer.vue';
import Alert from '@modules/husleie-framework/src/components/text/Alert.vue';
import Loader from '@modules/husleie-framework/src/components/loaders/Loader.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import { SessionTypeEnum } from '@modules/husleie-framework/src/types/Session.entity';
import App from '@modules/husleie-framework/src/static-classes/App.class.js';
import { ProfilesLandlordApi, InspectionReportApi, RentalObjectApi } from '@modules/husleieapi2';
import { getDefaultFlatStructure } from '@modules/husleie-framework/src/helpers/inspectionReport';
import Raven from 'raven-js';

const profilesLandlordApi = new ProfilesLandlordApi();
const inspectionReportApi = new InspectionReportApi();
const rentalObjectApi = new RentalObjectApi();

export default {
  props: {
    rentalObjectId: [Number, String],
  },

  components: {
    BlockContainer,
    'hl-Loader': Loader,
    'hl-Alert': Alert,
    'hl-Date': Date,
  },

  data() {
    return {
      statusList: [
        {
          name: 'Signert',
          value: 'signed',
        },
        {
          name: 'Klar til signering',
          value: 'ready_to_sign',
        },
        {
          name: 'Aktiv',
          value: 'active',
        },
        {
          name: 'Åpent',
          value: 'open',
        },
        {
          name: 'Åpent',
          value: 'draft',
        },
      ],

      tops: [],
      newTops: [],
      topsLoading: false,
      topsError: null,
      newInspectionReportsLoading: false,
      newInspectionReportsError: null,
      moveoutProtocols: [],
    };
  },

  computed: {
    ...mapGetters(['getRentalObject', 'getRentalObjectTenancies', 'isExtendedRentalObjectLoaded', 'getLandlordId']),

    rentalObject() {
      return this.getRentalObject(this.rentalObjectId);
    },

    loaded() {
      return this.isExtendedRentalObjectLoaded;
    },

    rentalObjectTops() {
      const products = [];

      for (const tenancy of this.getRentalObject(this.rentalObjectId).tenancies) {
        for (const product of tenancy.products) {
          if (product.name === 'top') {
            products.push(tenancy);
          }
        }
      }

      return products;
    },

    
    allTops() {
      return [...this.newTops, ...this.tops];
    },

    isLoadingTops() {
      return this.topsLoading || this.newInspectionReportsLoading;
    },

    isLoadingMoveouts() {
      return this.newInspectionReportsLoading;
    },

    hasErrors() {
      return this.topsError || this.newInspectionReportsError;
    }
  },

  watch: {
    loaded(newVal) {
      if (newVal) {
        this.fetchAllData();
      }
    },
    rentalObjectId() {
      if (this.loaded) {
        this.fetchAllData();
      }
    }
  },

  mounted() {
    if (this.loaded) {
      this.fetchAllData();
    }
  },

  methods: {
    async fetchTops() {
      this.topsLoading = true;
      this.topsError = null;
      
      try {
        const tops = [];

        for (const tenancy of this.rentalObjectTops) {
          for (const product of tenancy.products) {
            if (product.name === 'top') {
              const url =
                product.status !== 'signed'
                  ? RedirectOperations.getURL(AppRedirect.INSPECTION_REPORT_EDIT, {
                      inspectionReportType: SessionTypeEnum.INSPECTION,
                      uuid: product.id,
                      landlordId: this.getLandlordId,
                      isLegacy: true, // in the future, when IR funnel is a JS app -> use "tenancy.status === 'shadow'",
                    })
                  : `${App.getHostURL()}/dashboard/landlord/${this.getLandlordId}/tenancy/top/${product.id}/documents`;

              tops.push({
                id: product.id,
                startDate: tenancy?.details?.duration?.start || null,
                tenant: tenancy?.tenants?.[0]?.name || 'Ukjent leietaker',
                status: product.status,
                uri: url,
              });
            }
          }
        }

        this.tops = tops;
      } catch (error) {
        console.error('Error fetching tops:', error);
        this.topsError = 'Failed to load inspection reports';
        Raven.captureException(error);
      } finally {
        this.topsLoading = false;
      }
    },

    async fetchNewTops() {
      this.newInspectionReportsLoading = true;
      this.newInspectionReportsError = null;
      
      try {
        const inspectionReports = [];
        const moveoutProtocols = [];
        const allInspectionReports = await inspectionReportApi.getInspectionReports();
        const roId = Number(this.rentalObjectId);
        const rentalObjectInspectionReport = allInspectionReports.filter(ir => Number(ir.rentalObjectId) === roId);  
        rentalObjectInspectionReport.forEach(ir => {
          const url =
            ir.status === 'draft'
              ? `${App.getHostURL()}/dashboard/landlord/${ir.landlordId}/tenancy/${ir.tenancyId}/object/${ir.rentalObjectId}/inspection-report/${ir.id}`
              : `${App.getHostURL()}/dashboard/landlord/${ir.landlordId}/rentalObject/${ir.rentalObjectId}/tenancy/${ir.tenancyId}/documents`;

          if (ir.object?.reportType !== 'moveout') {
            inspectionReports.push({
              id: ir.id,
              startDate: ir.created,
              tenant: [ir?.object?.tenant_given_name, ir?.object?.tenant_family_name].filter(Boolean).join(' ') || 'Ukjent leietaker',
              status: ir.status,
              uri: url
            });
          } else {moveoutProtocols.push({
              id: ir.id,
              startDate: ir.created,
              tenant: [ir?.object?.tenant_given_name, ir?.object?.tenant_family_name].filter(Boolean).join(' ') || 'Ukjent leietaker',
              status: ir.status,
              uri: url
            })
          }
        });
        
        this.newTops = inspectionReports;
        this.moveoutProtocols = moveoutProtocols;
      } catch (error) {
        console.error('Error fetching new tops:', error);
        this.newInspectionReportsError = 'Failed to load new inspection reports';
        Raven.captureException(error);
      } finally {
        this.newInspectionReportsLoading = false;
      }
    },

    async fetchAllData() {
      // Fetch both tops and newTops in parallel
      await Promise.all([
        this.fetchTops(),
        this.fetchNewTops()
      ]);
    },

    mapStatuses(status) {
      for (const i of this.statusList) {
        if (i.value === status) {
          return i.name;
        }
      }
    },

    getInitialInspectionReportData(reportType = 'takeover') {
      const data = getDefaultFlatStructure(reportType);

      // We have access to landlord and rental object data from context
      const landlordId = this.getLandlordId;
      const rentalObjectId = this.rentalObjectId;

      // Note: This component operates in the context where we have a rental object
      // but no specific tenancy (since it shows when no TOPs exist)
      // The data will be prefilled async after API calls

      return data;
    },

    async createNewTopInspectionReport() {
      try {
        const landlordId = this.getLandlordId;
        const rentalObjectId = this.rentalObjectId;

        // Fetch landlord data for prefilling
        const landlordData = await profilesLandlordApi.getProfileLandlord(landlordId);
        
        // Fetch rental object data for prefilling  
        const rentalObjectData = await rentalObjectApi.getLandlordRentalObject(landlordId, rentalObjectId);

        // Get initial data structure
        const data = this.getInitialInspectionReportData('takeover');

        // Prefill landlord details
        if (landlordData) {
          const ll_fullName = landlordData.owner.name || '';
          const lastSpaceIndex = ll_fullName.lastIndexOf(' ');
          const ll_givenName = lastSpaceIndex > -1 ? ll_fullName.substring(0, lastSpaceIndex) : ll_fullName;
          const ll_familyName = lastSpaceIndex > -1 ? ll_fullName.substring(lastSpaceIndex + 1) : '';

          data.landlord_given_name = ll_givenName;
          data.landlord_family_name = ll_familyName;
          data.landlord_cell_phone = landlordData?.cellPhone || '';

          if (landlordData.addresses && Array.isArray(landlordData.addresses)) {
            const ll_address = landlordData.addresses.find(addr => addr.purpose === 'postal');
            if (ll_address) {
              data.landlord_email = ll_address.email || '';
            }
          }
        }

        // Prefill rental object details
        if (rentalObjectData) {
          data.rental_object_street = rentalObjectData.address.street;
          data.rental_object_postal_zipcode = rentalObjectData.address.postalZipcode;
          data.rental_object_postal_area = rentalObjectData.address.postalArea;
          data.rental_object_apartment_number = rentalObjectData.details.apartmentNumber;
          data.rental_object_type = rentalObjectData.details.type;

          if (rentalObjectData.details.roomNumber != '0') {
            data.rental_object_room_number = rentalObjectData.details.roomNumber;
          }
        }

        // Create the inspection report (no tenancy in this context)
        const newInspectionReport = await inspectionReportApi.createInspectionReport(
          landlordId,
          null, // no tenancy
          rentalObjectId,
          data
        );

        window.open(`/dashboard/landlord/${landlordId}/tenancy/0/object/${rentalObjectId}/inspection-report/${newInspectionReport.id}`, '_blank');

      } catch (error) {
        Raven.captureException(error);
        console.error('Failed to create inspection report:', error);
        // Could add user-facing error handling here
      }
    },
  },
};
</script>

<style scoped lang="scss">
.action-menu-icon {
  background: #f8f8f8;
  text-align: center;
  padding: 10px;
  border-radius: 50%;
  display: inline-block;
  border: 1px solid $gray-500;
  width: 30px;
  height: 30px;
  transition: 0.3s ease-in-out;
  cursor: pointer;

  i {
    font-size: 1.1rem;
    position: relative;
    top: -10px;
    left: -5px;
    color: $gray-800;
  }

  span {
    letter-spacing: 2px;
    position: relative;
    top: -7px;
    left: -3px;
    font-size: 0.8rem;
    font-weight: 600;
    color: $gray-800;
  }
}

.action-menu-icon:hover {
  box-shadow: $box-shadow-1;
  background: $gray-500;

  span {
    color: white;
  }
}
</style>
